const EXIT_START  = 0.78;
const EXIT_END    = 0.88;

const CONTENT_IN_START = 0.74;
const CONTENT_IN_END   = EXIT_END + 0.08;

// Headline/content fade-out is driven by real distance-to-unstick (px),
// not the `p` fraction — `p`'s assumed total scroll distance can drift from
// the actual sticky release point, which was fading things out while the
// section was still pinned (leaving a long blank hold before it unstuck).
const CLOSE_DISTANCE = 180;

const IMAGE_CONFIG = [
  { enterStart: 0.20, enterEnd: 0.32, startRotate: -22, endRotate:  -8, settleRotate: 0, parallaxEnd: -20, exitX: -2400, exitY:  -60, exitDelay: 0.00 },
  { enterStart: 0.40, enterEnd: 0.52, startRotate: -12, endRotate:  -10, settleRotate: 0, parallaxEnd: -14, exitX: 0,     exitY: -1400, exitDelay: 0.03 },
  { enterStart: 0.50, enterEnd: 0.62, startRotate:  10, endRotate:   0, settleRotate: 0, parallaxEnd: -18, exitX: 2400,  exitY:    0, exitDelay: 0.01 },
  { enterStart: 0.60, enterEnd: 0.72, startRotate:  28, endRotate:  12, settleRotate: 0, parallaxEnd: -10, exitX: 2400,  exitY: -120, exitDelay: 0.04 },
  { enterStart: 0.10, enterEnd: 0.22, startRotate:   8, endRotate:   0, settleRotate: 0, parallaxEnd:  -7, exitX: -2400, exitY:    0, exitDelay: 0.02 },
  { enterStart: 0.30, enterEnd: 0.42, startRotate:  -8, endRotate:   2, settleRotate: 0, parallaxEnd:  -4, exitX: -2400, exitY:   60, exitDelay: 0.05 },
];

class BackstoryScroll {
  constructor(el) {
    this.el = el;
    this.header = document.querySelector('sticky-header.header');
    this.headline = el.querySelector('.backstory__headline-wrap');
    this.images = Array.from(el.querySelectorAll('.backstory__image-wrap'));
    this.content = el.querySelector('.backstory__content');

    this.raf = null;
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  mapRange(value, inStart, inEnd, outStart, outEnd) {
    if (inEnd === inStart) return outStart;

    const t = Math.max(0, Math.min(1, (value - inStart) / (inEnd - inStart)));

    return outStart + (outEnd - outStart) * t;
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  easeOutExpo(t) {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }

  easeInQuart(t) {
    return t * t * t * t;
  }

  getProgress() {
    const rect = this.el.getBoundingClientRect();
    const total = this.el.offsetHeight - (window.innerHeight - 80);

    if (total <= 0) return 0;

    return Math.max(0, Math.min(1, (-rect.top + 80) / total));
  }

  update() {
    const p = this.getProgress();
    const rect = this.el.getBoundingClientRect();

    if (this.header) {
      const headerHeight = this.header.offsetHeight || 80;

      this.header.classList.toggle(
        'backstory-active',
        rect.top <= headerHeight && rect.bottom > headerHeight
      );
    }

    const hy = this.mapRange(p, 0, 0.05, 160, 0);
    const ho = this.mapRange(p, 0, 0.05, 0, 1); 
    const hs = this.mapRange(p, 0, 0.05, 0.85, 1);

    const distanceToUnstick = rect.bottom - window.innerHeight;

    const contentOutT = this.mapRange(distanceToUnstick, 80, -680, 0, 1);
    const headlineOutT = this.mapRange(distanceToUnstick, 80, -280, 0, 1);

    if (this.headline) {
      const headlineOpacity = Math.min(ho, 1 - headlineOutT);

      const headlineY = headlineOutT > 0
      ? headlineOutT
      : hy;

      this.headline.style.opacity = headlineOpacity;
      this.headline.style.transform = `translate(-50%, calc(-50% + ${headlineY}px))`;
    }

    this.images.forEach((img, i) => {
      const cfg = IMAGE_CONFIG[i];

      if (!cfg) return;

      const {
        enterStart,
        enterEnd,
        startRotate,
        endRotate,
        settleRotate = 0,
        parallaxEnd,
        exitX,
        exitY,
      } = cfg;

      let x = 0;
      let y = 0;
      let rotate = endRotate;
      let opacity = 1;
      let scale = 1;

      const imgExitStart = EXIT_START + (cfg.exitDelay || 0);
      const imgExitEnd   = EXIT_END   + (cfg.exitDelay || 0);

      if (p < enterEnd) {
        const rawT = this.mapRange(p, enterStart, enterEnd, 0, 1);
        const eased = this.easeOutExpo(rawT);

        const entryY = window.innerHeight * 0.22;
        y = entryY * (1 - eased);
        opacity = i === 0
          ? this.mapRange(p, enterStart, enterStart + 0.035, 0, 1)
          : rawT;

        rotate = startRotate + ((endRotate + settleRotate) - startRotate) * eased;
      } else if (p < imgExitStart) {
        const settleT = this.mapRange(p, enterEnd, enterEnd + 0.08, 0, 1);
        const easedSettle = this.easeOutCubic(settleT);

        y = this.mapRange(p, enterEnd, imgExitStart, 0, parallaxEnd);
        opacity = 1;
        rotate = endRotate + settleRotate * (1 - easedSettle);
      } else {
        const t = this.mapRange(p, imgExitStart, imgExitEnd, 0, 1);
        const easedT = this.easeInQuart(t);

        x = exitX * easedT;
        y = parallaxEnd + exitY * easedT;
        opacity = 1 - t;
        scale = 1 - easedT * 0.25;
        rotate = endRotate;
      }

      img.style.opacity = opacity;
      img.style.transform = `translate(${x}px, ${y}px) rotate(${rotate}deg) scale(${scale})`;
    });

    if (this.content) {
      const fadeIn = this.mapRange(p, CONTENT_IN_START, CONTENT_IN_END, 0, 1);
      const contentOpacity = Math.min(fadeIn, 1 - contentOutT);

      this.content.style.opacity = contentOpacity;
      this.content.style.transform = `translate(-50%, ${contentOutT * 80}px)`;
    }
  }

  onScroll() {
    if (this.raf) return;

    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.update();
    });
  }
}

function initBackstory() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('[data-backstory]').forEach((el) => {
    new BackstoryScroll(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBackstory);
} else {
  initBackstory();
}